import { useEffect, useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useCases } from '../data/content';

gsap.registerPlugin(ScrollTrigger);

export default function UseCaseCards() {
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Panel reveal
      gsap.fromTo(
        panelRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: panelRef.current,
            start: 'top 80%',
          },
        }
      );

      // Content fade in
      const contentItems = contentRef.current?.querySelectorAll('.content-reveal');
      if (contentItems) {
        gsap.fromTo(
          contentItems,
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: contentRef.current,
              start: 'top 80%',
            },
          }
        );
      }

      // Cards stagger with image scale reveal
      const cards = cardsRef.current?.querySelectorAll('.use-case-card');
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 85%',
            },
          }
        );

        // Image scale animation inside cards
        cards.forEach((card) => {
          const img = card.querySelector('.card-image');
          if (img) {
            gsap.fromTo(
              img,
              { scale: 1.15 },
              {
                scale: 1,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: card,
                  start: 'top 85%',
                },
              }
            );
          }
        });
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <section id="use-cases" className="section-container py-8 sm:py-14" aria-labelledby="use-case-title">
      <div ref={panelRef} className="liquid-panel grid gap-8 p-6 sm:p-8 lg:grid-cols-[0.88fr_1.12fr] lg:p-10 opacity-0">
        <div ref={contentRef} className="flex flex-col justify-center">
          <p className="content-reveal text-sm font-extrabold uppercase tracking-[0.25em] text-violet-600 opacity-0">Use cases</p>
          <h2 id="use-case-title" className="content-reveal mt-3 text-3xl font-extrabold tracking-[-0.04em] text-slate-950 sm:text-5xl opacity-0">Designed for the way people actually live and work.</h2>
          <p className="content-reveal mt-5 text-lg leading-8 text-slate-600 opacity-0">Relate.ai adapts across every part of your life and work — so you can focus on what matters most.</p>
          <a href="#early-access" className="content-reveal secondary-button mt-8 w-fit gap-2 opacity-0">Explore Use Cases <ArrowUpRight size={18} aria-hidden="true" /></a>
        </div>
        <div ref={cardsRef} className="grid gap-4 sm:grid-cols-2">
          {useCases.map(({ title, body, icon: Icon, gradient }) => (
            <article key={title} className="use-case-card glass-card overflow-hidden rounded-[1.75rem] bg-white/75 p-4 transition duration-300 hover:-translate-y-1 hover:bg-white opacity-0">
              <div className={`card-image mb-4 h-32 rounded-[1.35rem] bg-gradient-to-br ${gradient} p-4 overflow-hidden`}>
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/80 text-violet-700 shadow-glass">
                  <Icon size={25} aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-lg font-extrabold text-slate-950">{title}</h3>
              <p className="mt-2 min-h-16 text-sm leading-6 text-slate-600">{body}</p>
              <a href="#early-access" className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-violet-700">Learn more <ArrowUpRight size={16} aria-hidden="true" /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
